import { createServerFn } from '@tanstack/react-start';
import {
    and,
    arrayContains,
    count,
    desc,
    eq,
    gte,
    ilike,
    inArray,
    isNull,
    not,
    or,
    sql,
} from 'drizzle-orm';
import { z } from 'zod';

import { getDb } from '@/db/client';
import { articles, externalAlerts, type ExternalAlertRow } from '@/db/schema';
import { fillDailySeries, fillDailySeriesBetween, jerusalemDayKey, jerusalemMonthKey } from '@/lib/analytics-series';
import {
    paceStatsFromDaily,
    rankOutletsByCount,
    type OutletRank,
} from '@/lib/competitor-ranking';
import {
    COMPETITORS,
    COMPETITOR_PROVIDER_IDS,
    EXTERNAL_ALERT_CATEGORY,
    EXTERNAL_CATEGORY_KEY,
    EXTERNAL_NEWS_WINDOW_DAYS,
} from '@/lib/external-news';
import { requireAdminAccess } from '@/server/functions/auth';

export type CompetitorRow = {
    id: string;
    title: string;
    provider: string;
    providerLabel: string;
    url: string;
    date: Date;
};

export type CompetitorDailySeries = {
    id: string;
    label: string;
    data: { day: string; count: number }[];
};

export type CompetitorDailyStats = {
    days: number;
    ours: CompetitorDailySeries;
    competitors: CompetitorDailySeries[];
    mtd: {
        monthKey: string;
        ranking: OutletRank[];
    };
};

const listSchema = z.object({
    q: z.string().optional(),
    provider: z.string().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});

const alertWhen = sql`COALESCE(${externalAlerts.publishedAt}, ${externalAlerts.firstSeenAt})`;

function externalAlertsInWindow(since: Date) {
    return or(
        gte(externalAlerts.publishedAt, since),
        and(
            isNull(externalAlerts.publishedAt),
            gte(externalAlerts.firstSeenAt, since),
        ),
    );
}

function competitorAlertFilters(provider?: string) {
    const filters = [
        eq(externalAlerts.alertCategory, EXTERNAL_ALERT_CATEGORY.local),
        inArray(externalAlerts.provider, COMPETITOR_PROVIDER_IDS),
    ];

    if (provider && provider !== 'all') {
        filters.push(eq(externalAlerts.provider, provider));
    }

    return filters;
}

function toCompetitorRow(row: ExternalAlertRow): CompetitorRow {
    return {
        id: row.id,
        title: row.title,
        provider: row.provider,
        providerLabel: row.providerLabel,
        url: row.url,
        date: row.publishedAt ?? row.firstSeenAt,
    };
}

export const adminListCompetitorsFn = createServerFn({ method: 'GET' })
    .validator(listSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const limit = data.limit ?? 50;
        const offset = data.offset ?? 0;
        const since = new Date(
            Date.now() - EXTERNAL_NEWS_WINDOW_DAYS * 24 * 60 * 60 * 1000,
        );

        const filters = [
            ...competitorAlertFilters(data.provider),
            externalAlertsInWindow(since),
        ];

        if (data.q?.trim()) {
            const q = `%${data.q.trim()}%`;
            filters.push(
                or(
                    ilike(externalAlerts.title, q),
                    ilike(externalAlerts.providerLabel, q),
                    ilike(externalAlerts.provider, q),
                    ilike(externalAlerts.url, q),
                )!,
            );
        }

        const where = and(...filters);
        const rows = await db
            .select()
            .from(externalAlerts)
            .where(where)
            .orderBy(desc(alertWhen))
            .limit(limit)
            .offset(offset);
        const total = await db
            .select({ value: count() })
            .from(externalAlerts)
            .where(where);

        return {
            rows: rows.map(toCompetitorRow),
            total: total[0]?.value ?? 0,
        };
    });

export const adminCompetitorsDailyStatsFn = createServerFn({ method: 'GET' })
    .validator(z.object({}))
    .handler(async () => {
        await requireAdminAccess();
        const db = getDb();
        const days = EXTERNAL_NEWS_WINDOW_DAYS;
        const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        const ourDayExpr = sql<string>`to_char(${articles.date} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD')`;
        const alertDayExpr = sql<string>`to_char(${alertWhen} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD')`;
        const now = new Date();
        const monthKey = jerusalemMonthKey(now);
        const mtdStart = `${monthKey}-01`;
        const mtdEnd = jerusalemDayKey(now);
        const ourMtdFilter = sql`(to_char(${articles.date} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD') >= ${mtdStart}
          and to_char(${articles.date} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD') <= ${mtdEnd})`;
        const alertMtdFilter = sql`(to_char(${alertWhen} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD') >= ${mtdStart}
          and to_char(${alertWhen} AT TIME ZONE 'Asia/Jerusalem', 'YYYY-MM-DD') <= ${mtdEnd})`;

        const [ourRows, competitorRows, ourMtdRows, competitorMtdRows] =
            await Promise.all([
                db
                    .select({ day: ourDayExpr, value: count() })
                    .from(articles)
                    .where(
                        and(
                            eq(articles.status, 'published'),
                            not(
                                arrayContains(articles.categoryKeys, [
                                    EXTERNAL_CATEGORY_KEY,
                                ]),
                            ),
                            gte(articles.date, since),
                        ),
                    )
                    .groupBy(ourDayExpr)
                    .orderBy(ourDayExpr),
                db
                    .select({
                        day: alertDayExpr,
                        provider: externalAlerts.provider,
                        value: count(),
                    })
                    .from(externalAlerts)
                    .where(
                        and(
                            eq(externalAlerts.alertCategory, EXTERNAL_ALERT_CATEGORY.local),
                            inArray(externalAlerts.provider, COMPETITOR_PROVIDER_IDS),
                            externalAlertsInWindow(since),
                        ),
                    )
                    .groupBy(alertDayExpr, externalAlerts.provider)
                    .orderBy(alertDayExpr),
                db
                    .select({ day: ourDayExpr, value: count() })
                    .from(articles)
                    .where(
                        and(
                            eq(articles.status, 'published'),
                            not(
                                arrayContains(articles.categoryKeys, [
                                    EXTERNAL_CATEGORY_KEY,
                                ]),
                            ),
                            ourMtdFilter,
                        ),
                    )
                    .groupBy(ourDayExpr)
                    .orderBy(ourDayExpr),
                db
                    .select({
                        day: alertDayExpr,
                        provider: externalAlerts.provider,
                        value: count(),
                    })
                    .from(externalAlerts)
                    .where(
                        and(
                            eq(externalAlerts.alertCategory, EXTERNAL_ALERT_CATEGORY.local),
                            inArray(externalAlerts.provider, COMPETITOR_PROVIDER_IDS),
                            alertMtdFilter,
                        ),
                    )
                    .groupBy(alertDayExpr, externalAlerts.provider)
                    .orderBy(alertDayExpr),
            ]);

        const byProvider = new Map<string, { day: string; value: number }[]>();
        for (const row of competitorRows) {
            const provider = row.provider?.trim();
            if (!provider) continue;
            const bucket = byProvider.get(provider) ?? [];
            bucket.push({ day: row.day, value: Number(row.value) });
            byProvider.set(provider, bucket);
        }

        const mtdByProvider = new Map<string, { day: string; value: number }[]>();
        for (const row of competitorMtdRows) {
            const provider = row.provider?.trim();
            if (!provider) continue;
            const bucket = mtdByProvider.get(provider) ?? [];
            bucket.push({ day: row.day, value: Number(row.value) });
            mtdByProvider.set(provider, bucket);
        }

        return {
            days,
            ours: {
                id: 'ours',
                label: 'אשקלון ניוז',
                data: fillDailySeries(days, ourRows),
            },
            competitors: COMPETITORS.map((item) => ({
                id: item.id,
                label: item.label,
                data: fillDailySeries(days, byProvider.get(item.id) ?? []),
            })),
            mtd: {
                monthKey,
                ranking: rankOutletsByCount([
                    {
                        id: 'ours',
                        label: 'אשקלון ניוז',
                        isOurs: true,
                        ...paceStatsFromDaily(
                            fillDailySeriesBetween(
                                mtdStart,
                                mtdEnd,
                                ourMtdRows,
                            ),
                        ),
                    },
                    ...COMPETITORS.map((item) => ({
                        id: item.id,
                        label: item.label,
                        ...paceStatsFromDaily(
                            fillDailySeriesBetween(
                                mtdStart,
                                mtdEnd,
                                mtdByProvider.get(item.id) ?? [],
                            ),
                        ),
                    })),
                ]),
            },
        } satisfies CompetitorDailyStats;
    });

export const adminDeleteCompetitorFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [row] = await db
            .select({ id: externalAlerts.id })
            .from(externalAlerts)
            .where(
                and(
                    eq(externalAlerts.id, data.id),
                    eq(externalAlerts.alertCategory, EXTERNAL_ALERT_CATEGORY.local),
                    inArray(externalAlerts.provider, COMPETITOR_PROVIDER_IDS),
                ),
            )
            .limit(1);
        if (!row) {
            throw new Error('הכותרת לא נמצאה');
        }
        await db.delete(externalAlerts).where(eq(externalAlerts.id, data.id));
        return { ok: true };
    });
